import * as React from 'react';
import { t } from '@reos/i18n';
import OlMap from 'ol/Map.js';
import View from 'ol/View.js';
import TileLayer from 'ol/layer/Tile.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import TileWMS from 'ol/source/TileWMS.js';
import TileArcGISRest from 'ol/source/TileArcGISRest.js';
import type { Layer } from 'ol/layer.js';
import { Style, Stroke, Fill } from 'ol/style.js';
import type { MapBrowserEvent } from 'ol/index.js';
import { getRenderPixel } from 'ol/render.js';
import { unByKey } from 'ol/Observable.js';
import type RenderEvent from 'ol/render/Event.js';
import type { ClientMapLayerId } from '@reos/duediligence';
import {
  registerSjtskProjection,
  EPSG_5514,
  sjtskToWgs84,
  type SjtskPoint,
} from '../lib/parcel-map-crs.js';
import {
  geoJsonToParcelFeatures,
  type CaseGeometryCollectionJson,
} from '../lib/parcel-map-geojson.js';
import {
  swipeClipRect,
  swipeClipRectLeft,
  type SwipeClipRect,
  type SwipeClipSize,
} from '../lib/parcel-map-swipe.js';
import {
  fetchCaseGeometry,
  fetchClientMapLayers,
  fetchParcelAtPoint,
  type ClientMapLayerDto,
} from '../lib/parcel-map-api.js';
import {
  LAYER_LABEL_KEY,
  layerStateToSearch,
  parseLayerStateFromSearch,
  thematicLayers,
  type LayerVisibilityState,
} from '../lib/map-layer-catalog.js';
import { MapLayersPanel } from './MapLayersPanel.js';
import { ParcelProximityPanel } from './ParcelProximityPanel.js';
import 'ol/ol.css';

/**
 * Krok M1 — interaktivni mapova karta (OpenLayers, EPSG:5514 - vzor
 * Pozemkov, brief ROZHODNUTI). Vrstvy chodi z `GET .../map/layers`
 * (registr, zadna URL natvrdo), geometrie parcely z
 * `GET .../cases/:id/geometry.geojson`.
 *
 * URL stavu mapy (`?parcelId=&x=&y=&zoom=`) jde primo pres
 * `history.replaceState`, ne pres typovany `search` routy zakazky (ten nese
 * jen `sekce` - rozsireni jeho schematu by ovlivnilo cely router; tady jde o
 * doplnkovy, aditivni stav sdilitelneho odkazu).
 */

const DEFAULT_ZOOM = 17;

/** MAP4: nazvy vrstev zije v `lib/map-layer-catalog.ts` (sdili je legenda i panel vrstev). */

/** W6: prekryvy, ktere jsou VOLITELNE (vychozi skryte, na rozdil od katastru/ortofota). */
const OPTIONAL_OVERLAY_IDS = ['buildings', 'protected_areas'] as const;
type OptionalOverlayId = (typeof OPTIONAL_OVERLAY_IDS)[number];

const OPTIONAL_OVERLAY_TOGGLE_KEYS: Record<
  OptionalOverlayId,
  { readonly show: Parameters<typeof t>[0]; readonly hide: Parameters<typeof t>[0] }
> = {
  buildings: {
    show: 'webAdmin.parcelMap.tools.showBuildings',
    hide: 'webAdmin.parcelMap.tools.hideBuildings',
  },
  protected_areas: {
    show: 'webAdmin.parcelMap.tools.showProtectedAreas',
    hide: 'webAdmin.parcelMap.tools.hideProtectedAreas',
  },
};

function buildLayerSource(dto: ClientMapLayerDto): TileWMS | TileArcGISRest | null {
  if (dto.status !== 'available' || dto.url === null) return null;
  const params = {
    LAYERS: dto.layerNames.join(','),
    VERSION: dto.version,
    TRANSPARENT: true,
    FORMAT: 'image/png',
  };
  return dto.kind === 'arcgis_rest'
    ? new TileArcGISRest({ url: dto.url, params: { LAYERS: `show:${dto.layerNames.join(',')}` } })
    : new TileWMS({ url: dto.url, params, projection: EPSG_5514 });
}

/** Krok AKV1: sdileno s mapou uzemi akvizice (`TerritoryMap.tsx`) — jedna stavba vrstev z registru. */
export function buildLayer(dto: ClientMapLayerDto): Layer | null {
  const source = buildLayerSource(dto);
  if (source === null) return null;
  const layer = new TileLayer({ source });
  layer.set('parcelMapLayerId', dto.id);
  return layer;
}

/**
 * M1b swipe — `orthophoto_archive.layerNames` jsou volitelne ROCNIKY (viz
 * `client-layers.ts` v @reos/duediligence), ne vrstvy k soucasnemu prekryti.
 * Sklada zdroj pro JEDEN zvoleny rocnik; `null` kdyz vrstva neni dostupna
 * nebo zvoleny rocnik neni v registru (nikdy neplatna URL - T1b).
 */
function buildArchiveYearSource(dto: ClientMapLayerDto, year: string): TileWMS | null {
  if (dto.status !== 'available' || dto.url === null || !dto.layerNames.includes(year)) return null;
  return new TileWMS({
    url: dto.url,
    params: { LAYERS: year, VERSION: dto.version, TRANSPARENT: true, FORMAT: 'image/png' },
    projection: EPSG_5514,
  });
}

const SWIPE_CURRENT_YEAR = 'current';

/** Ořezová clip funkce (levá/pravá strana) — sdílená geometrie s `parcel-map-swipe.spec.ts`. */
type SwipeRectFn = (size: SwipeClipSize, position: number) => SwipeClipRect;

/** OL prerender/postrender clip podle obdélníku ze `swipeClipRect`/`swipeClipRectLeft`. */
function clipLayerToRect(
  map: OlMap,
  layer: TileLayer,
  rectFn: SwipeRectFn,
  position: number,
): () => void {
  const preKey = layer.on('prerender', (event: RenderEvent) => {
    const size = map.getSize();
    if (size === undefined || event.context === undefined) return;
    const [widthPx = 0, heightPx = 0] = size;
    const rect = rectFn({ widthPx, heightPx }, position);
    const ctx = event.context as CanvasRenderingContext2D;
    const topLeft = getRenderPixel(event, [rect.x, rect.y]);
    const bottomRight = getRenderPixel(event, [rect.x + rect.width, rect.y + rect.height]);
    ctx.save();
    ctx.beginPath();
    ctx.rect(
      topLeft[0] ?? 0,
      topLeft[1] ?? 0,
      (bottomRight[0] ?? 0) - (topLeft[0] ?? 0),
      (bottomRight[1] ?? 0) - (topLeft[1] ?? 0),
    );
    ctx.clip();
  });
  const postKey = layer.on('postrender', (event: RenderEvent) => {
    (event.context as CanvasRenderingContext2D | undefined)?.restore();
  });
  return () => {
    unByKey(preKey);
    unByKey(postKey);
  };
}

function updateUrlState(point: SjtskPoint | null, zoom: number, parcelId: string | null): void {
  const url = new URL(window.location.href);
  if (point !== null) {
    url.searchParams.set('x', point.x.toFixed(1));
    url.searchParams.set('y', point.y.toFixed(1));
  }
  url.searchParams.set('zoom', String(Math.round(zoom)));
  if (parcelId !== null) url.searchParams.set('parcelId', parcelId);
  window.history.replaceState(window.history.state as unknown, '', url);
}

export interface ParcelMapProps {
  readonly caseId: string;
  /** Case detail uz nese `parcel.identity` fakt - karta jen zapise jeho label do URL. */
  readonly parcelId?: string | null;
  /** Krok M1 bod 3: panel potrebuje vymeru z geometrie (KN vs. geometrie "Shoda"). */
  readonly onGeometryLoaded?: ((areaM2: number | null) => void) | undefined;
  /**
   * Krok L1: parcely sdruzene nabidky VYBARVENE podle stavu (vzor Nahlizeni do
   * KN). Volitelne - bez nich se mapa chova presne jako pred krokem L1.
   */
  readonly offerGeometry?: OfferGeometryCollectionJson | null | undefined;
  /**
   * Krok L1: klik do mapy vrati parcelu pod bodem (K0c resolver na serveru).
   * Panel nabidky z ni PREDVYPLNI pole - nikdy neprida sam, protoze pridani
   * parcely muze stat penize.
   */
  readonly onParcelPicked?:
    ((parcel: { readonly label: string; readonly kuCode: number | null }) => void) | undefined;
  /**
   * Krok UXR5 bod 9 — KOMPAKTNI NAHLED (pravy sloupec detailu zakazky).
   *
   * Tyz renderer, tytez vrstvy, tataz geometrie — jen bez naradi: nemá
   * lištu nástrojů, legendu, souřadnice ani srovnávací posuvník, je nizky a
   * NEZAPISUJE stav do URL (nahled na zalozce „Přehled" nema prepisovat
   * adresu, kterou si makler posila). Predloha `Zakazka.dc.html` ma vpravo
   * ortofoto s hranicemi parcel; do UXR5 tam byl prazdny podklad s teckou
   * adresy (`MapPreview`), protoze hotovy vyrez K2 u zakazky casto neni.
   */
  readonly compact?: boolean;
}

/** Stavy parcel nabidky, ktere mapa umi vybarvit (`dd.offer.state.*`). */
export interface OfferGeometryFeatureJson {
  readonly type: 'Feature';
  readonly geometry: unknown;
  readonly properties: {
    readonly label: string;
    readonly state:
      'LV_IN_OFFER' | 'LV_OUT_OF_OFFER' | 'ADDED_OUTSIDE_LV' | 'OUTSIDE_LV_UNVERIFIED';
    readonly selected: boolean;
  };
}

export interface OfferGeometryCollectionJson {
  readonly type: 'FeatureCollection';
  readonly features: readonly OfferGeometryFeatureJson[];
}

/**
 * Barvy stavu vyberu. Vychazi z toho, jak vybarvuje Nahlizeni do KN (vybrana
 * nemovitost vyrazne, ostatni slabe), jen s nasimi stavy:
 *   v nabidce            - plna cervena (je to predmet prodeje),
 *   z LV mimo nabidku    - seda tenka (je na listu, ale neprodava se),
 *   pridana mimo LV      - modra (jiny puvod, tedy jina barva),
 *   mimo LV neoverena    - prerusovana oranzova (jeste nesmi do nabidky).
 */
const OFFER_STATE_STYLE: Record<
  OfferGeometryFeatureJson['properties']['state'],
  { stroke: string; width: number; fill: string; dash?: number[] }
> = {
  LV_IN_OFFER: { stroke: '#d4351c', width: 4, fill: 'rgba(212,53,28,0.16)' },
  LV_OUT_OF_OFFER: { stroke: '#94a3b8', width: 2, fill: 'rgba(148,163,184,0.08)' },
  ADDED_OUTSIDE_LV: { stroke: '#1d3fb8', width: 4, fill: 'rgba(29,63,184,0.16)' },
  OUTSIDE_LV_UNVERIFIED: {
    stroke: '#b45309',
    width: 3,
    fill: 'rgba(180,83,9,0.10)',
    dash: [8, 6],
  },
};

/**
 * Krok MAP3b — vyrez na geometrii parcely. JEDNO misto pro prvni zobrazeni
 * mapy i pro tlacitko „Přiblížit na parcelu" (`zoomToParcel`, uz existovalo):
 * dve kopie okraju a stropu zoomu by znamenaly, ze tlacitko casem ukaze jiny
 * vyrez nez otevreni zakazky.
 */
function fitParcelExtent(map: OlMap, extent: readonly number[]): void {
  map.getView().fit([...extent], { padding: [60, 60, 60, 60], maxZoom: 19 });
}

function offerFeatureStyle(feature: { get(key: string): unknown }): Style {
  const state = feature.get('state');
  const tokens =
    typeof state === 'string' && state in OFFER_STATE_STYLE
      ? OFFER_STATE_STYLE[state as OfferGeometryFeatureJson['properties']['state']]
      : OFFER_STATE_STYLE.LV_OUT_OF_OFFER;
  return new Style({
    stroke: new Stroke({
      color: tokens.stroke,
      width: tokens.width,
      ...(tokens.dash === undefined ? {} : { lineDash: tokens.dash }),
    }),
    fill: new Fill({ color: tokens.fill }),
  });
}

export function ParcelMap({
  caseId,
  parcelId = null,
  onGeometryLoaded,
  offerGeometry = null,
  onParcelPicked,
  compact = false,
}: ParcelMapProps): React.JSX.Element {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<OlMap | null>(null);
  const cadastralLayerRef = React.useRef<Layer | null>(null);
  /** MAP4: vsechny prekryvne vrstvy podle id - panel vrstev jim meni viditelnost i pruhlednost. */
  const overlayLayerRefs = React.useRef<Partial<Record<ClientMapLayerId, Layer>>>({});
  const swipeLeftLayerRef = React.useRef<TileLayer | null>(null);
  const swipeRightLayerRef = React.useRef<TileLayer | null>(null);
  const swipeClipCleanupRef = React.useRef<(() => void) | null>(null);
  const [layers, setLayers] = React.useState<readonly ClientMapLayerDto[] | null>(null);
  const [geometry, setGeometry] = React.useState<CaseGeometryCollectionJson | null>(null);
  const [hideCadastral, setHideCadastral] = React.useState(false);
  /**
   * W6: volitelne prekryvy jsou vychozi VYPNUTE. MAP4 k viditelnosti pridal
   * pruhlednost a rozsiril stav na VSECHNY tematicke vrstvy z registru (panel
   * vrstev), ne jen na dve napevno vyjmenovane. Vychozi stav cte URL, takze
   * odkaz na mapu nese zapnute vrstvy - cte se v renderu, ne v efektu.
   */
  const [layerState, setLayerState] = React.useState<LayerVisibilityState>(() =>
    parseLayerStateFromSearch(typeof window === 'undefined' ? '' : window.location.search),
  );
  const [zoom, setZoom] = React.useState<number | null>(null);
  const [fullscreen, setFullscreen] = React.useState(false);
  const [cursor, setCursor] = React.useState<{
    readonly sjtsk: SjtskPoint;
    readonly wgs84: { lat: number; lon: number };
  } | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [swipeEnabled, setSwipeEnabled] = React.useState(false);
  const [swipePosition, setSwipePosition] = React.useState(50);
  const [swipeYearLeft, setSwipeYearLeft] = React.useState('');
  const [swipeYearRight, setSwipeYearRight] = React.useState(SWIPE_CURRENT_YEAR);

  React.useEffect(() => {
    let cancelled = false;
    Promise.all([fetchClientMapLayers(), fetchCaseGeometry(caseId)])
      .then(([layerList, geo]) => {
        if (cancelled) return;
        setLayers(layerList);
        setGeometry(geo);
        const total = geo.features.reduce((sum, f) => sum + (f.properties.areaM2 ?? 0), 0);
        onGeometryLoaded?.(geo.features.length > 0 ? total : null);
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoadError(error instanceof Error ? error.message : String(error));
      });
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  const archiveLayer = layers?.find((l) => l.id === 'orthophoto_archive') ?? null;
  const archiveYears = archiveLayer?.status === 'available' ? archiveLayer.layerNames : [];

  React.useEffect(() => {
    if (archiveYears.length > 0 && swipeYearLeft === '') {
      const firstYear = archiveYears[0];
      if (firstYear !== undefined) setSwipeYearLeft(firstYear);
    }
  }, [archiveYears, swipeYearLeft]);

  React.useEffect(() => {
    if (containerRef.current === null || layers === null) return;
    registerSjtskProjection();

    const tileLayers = layers.map(buildLayer).filter((l): l is Layer => l !== null);
    const cadastral = layers.find((l) => l.id === 'cadastral');
    const cadastralLayer =
      tileLayers.find((l) => l.get('parcelMapLayerId') === cadastral?.id) ?? null;
    cadastralLayerRef.current = cadastralLayer;

    // W6: budovy a chranena uzemi jsou volitelne, vychozi skryte - na rozdil
    // od katastru/ortofota se nezobrazuji, dokud je uzivatel nezapne. MAP4:
    // stejne to plati pro kazdou tematickou vrstvu z katalogu (panel vrstev),
    // vcetne pruhlednosti; katastr si ridi vlastni prepinac `hideCadastral`.
    const overlayRefs: Partial<Record<ClientMapLayerId, Layer>> = {};
    const overlayIds: readonly ClientMapLayerId[] = [
      ...OPTIONAL_OVERLAY_IDS,
      ...thematicLayers(layers)
        .map((l) => l.id as ClientMapLayerId)
        .filter((id) => id !== 'cadastral'),
    ];
    for (const id of new Set(overlayIds)) {
      const overlayLayer = tileLayers.find((l) => l.get('parcelMapLayerId') === id);
      if (overlayLayer === undefined) continue;
      overlayLayer.setVisible(layerState.has(id));
      overlayLayer.setOpacity(layerState.get(id) ?? 1);
      overlayRefs[id] = overlayLayer;
    }
    overlayLayerRefs.current = overlayRefs;

    const swipeLeft = new TileLayer({ visible: false, zIndex: 50 });
    const swipeRight = new TileLayer({ visible: false, zIndex: 50 });
    swipeLeftLayerRef.current = swipeLeft;
    swipeRightLayerRef.current = swipeRight;

    const vectorSource = new VectorSource({
      features: geometry !== null ? geoJsonToParcelFeatures(geometry) : [],
    });
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      style: new Style({
        stroke: new Stroke({ color: '#16a34a', width: 3 }),
        fill: new Fill({ color: 'rgba(22,163,74,0.12)' }),
      }),
    });

    /**
     * Krok L1: parcely nabidky jako VLASTNI vrstva nad zamcenou geometrii -
     * nemichaji se do stejneho zdroje, protoze maji jiny zivotni cyklus
     * (pracovni vyber vs. uzamceny tvar) a jine barvy podle stavu.
     */
    const offerSource = new VectorSource({
      features:
        offerGeometry === null
          ? []
          : geoJsonToParcelFeatures(offerGeometry as unknown as CaseGeometryCollectionJson),
    });
    const offerLayer = new VectorLayer({
      source: offerSource,
      style: offerFeatureStyle,
      zIndex: 40,
    });

    const extentSource = offerSource.getFeatures().length > 0 ? offerSource : vectorSource;
    const extent = extentSource.getFeatures().length > 0 ? extentSource.getExtent() : null;
    const [exMinX = 0, exMinY = 0, exMaxX = 0, exMaxY = 0] = extent ?? [];
    const center =
      extent !== null ? [(exMinX + exMaxX) / 2, (exMinY + exMaxY) / 2] : [-745000, -1045000];

    const map = new OlMap({
      target: containerRef.current,
      layers: [...tileLayers, vectorLayer, offerLayer, swipeLeft, swipeRight],
      view: new View({ projection: EPSG_5514, center, zoom: DEFAULT_ZOOM }),
    });
    if (extent !== null) fitParcelExtent(map, extent);
    mapRef.current = map;

    map.on('click', (event: MapBrowserEvent) => {
      if (compact) return;
      const [x, y] = event.coordinate;
      if (x === undefined || y === undefined) return;
      const sjtsk = { x, y };
      setCursor({ sjtsk, wgs84: sjtskToWgs84(sjtsk) });
      updateUrlState(sjtsk, map.getView().getZoom() ?? DEFAULT_ZOOM, parcelId);
      /**
       * Krok L1: klik KAMKOLI do mapy -> parcela pod bodem (K0c resolver na
       * serveru, klient s CUZK nikdy nemluvi sam). Chyba resolveru je bezny
       * provozni stav (bod mimo digitalni mapu), ne duvod k cervene hlasce.
       */
      if (onParcelPicked !== undefined) {
        void fetchParcelAtPoint(x, y)
          .then((result) => {
            if (result.parcel === null) return;
            onParcelPicked({
              label: result.parcel.parcelNumber,
              kuCode: result.parcel.cadastralAreaCode,
            });
          })
          .catch(() => undefined);
      }
    });
    map.on('moveend', () => {
      if (compact) return;
      const view = map.getView();
      // MAP4: panel vrstev z toho pozna, ze vrstva je jeste pod svym `min_zoom`.
      setZoom(view.getZoom() ?? null);
      const c = view.getCenter();
      if (c === undefined) return;
      updateUrlState({ x: c[0] ?? 0, y: c[1] ?? 0 }, view.getZoom() ?? DEFAULT_ZOOM, parcelId);
    });

    return () => {
      swipeClipCleanupRef.current?.();
      swipeClipCleanupRef.current = null;
      swipeLeftLayerRef.current = null;
      swipeRightLayerRef.current = null;
      map.setTarget(undefined);
      mapRef.current = null;
    };
  }, [layers, geometry, parcelId, offerGeometry, onParcelPicked, compact]);

  React.useEffect(() => {
    cadastralLayerRef.current?.setVisible(!hideCadastral);
  }, [hideCadastral]);

  React.useEffect(() => {
    for (const [id, layer] of Object.entries(overlayLayerRefs.current)) {
      const typedId = id as ClientMapLayerId;
      layer?.setVisible(layerState.has(typedId));
      layer?.setOpacity(layerState.get(typedId) ?? 1);
    }
  }, [layerState]);

  /**
   * MAP4: zapnute vrstvy a jejich pruhlednost jsou v URL, takze odkaz na mapu
   * nese to, co je videt. `replaceState` (ne `pushState`) - prepnuti vrstvy
   * neni krok v historii, ktery by mel uzivatel prochazet tlacitkem zpet.
   */
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const search = layerStateToSearch(window.location.search, layerState);
    if (search === window.location.search) return;
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${search}`);
  }, [layerState]);

  const toggleOverlay = (id: ClientMapLayerId, defaultOpacity: number): void => {
    setLayerState((current) => {
      const next = new Map(current);
      if (next.has(id)) next.delete(id);
      else next.set(id, defaultOpacity);
      return next;
    });
  };

  const setOverlayOpacity = (id: ClientMapLayerId, opacity: number): void => {
    setLayerState((current) => {
      if (!current.has(id)) return current;
      const next = new Map(current);
      next.set(id, opacity);
      return next;
    });
  };

  const currentOrthophotoLayer = layers?.find((l) => l.id === 'orthophoto') ?? null;

  React.useEffect(() => {
    const map = mapRef.current;
    const left = swipeLeftLayerRef.current;
    const right = swipeRightLayerRef.current;
    if (map === null || left === null || right === null) return;

    if (!swipeEnabled || archiveLayer === null) {
      left.setVisible(false);
      right.setVisible(false);
      return;
    }

    const leftSource = buildArchiveYearSource(archiveLayer, swipeYearLeft);
    const rightSource =
      swipeYearRight === SWIPE_CURRENT_YEAR
        ? currentOrthophotoLayer !== null
          ? buildLayerSource(currentOrthophotoLayer)
          : null
        : buildArchiveYearSource(archiveLayer, swipeYearRight);

    if (leftSource === null || rightSource === null) {
      left.setVisible(false);
      right.setVisible(false);
      return;
    }

    left.setSource(leftSource);
    right.setSource(rightSource);
    left.setVisible(true);
    right.setVisible(true);

    const position = Math.min(1, Math.max(0, swipePosition / 100));
    const cleanupLeft = clipLayerToRect(map, left, swipeClipRectLeft, position);
    const cleanupRight = clipLayerToRect(map, right, swipeClipRect, position);
    swipeClipCleanupRef.current = () => {
      cleanupLeft();
      cleanupRight();
    };
    map.render();

    return () => {
      cleanupLeft();
      cleanupRight();
    };
  }, [
    swipeEnabled,
    swipePosition,
    swipeYearLeft,
    swipeYearRight,
    archiveLayer,
    currentOrthophotoLayer,
    layers,
    geometry,
    parcelId,
  ]);

  const zoomToParcel = (): void => {
    const map = mapRef.current;
    if (map === null || geometry === null || geometry.features.length === 0) return;
    const source = new VectorSource({ features: geoJsonToParcelFeatures(geometry) });
    const extent = source.getExtent();
    /* MAP3b: tytez okraje a strop zoomu jako prvni zobrazeni mapy — jeden
       `fitParcelExtent`, aby tlacitko neukazovalo jiny vyrez nez otevreni. */
    if (extent !== null) fitParcelExtent(map, extent);
  };

  return (
    <div
      data-testid="parcel-map"
      style={
        fullscreen
          ? { position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--rh-card)', padding: 12 }
          : undefined
      }
    >
      {!compact && (
      <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="parcel-map-toolbar">
        <button
          type="button"
          className="rh-btn"
          onClick={zoomToParcel}
          data-testid="parcel-map-zoom-to-parcel"
        >
          {t('webAdmin.parcelMap.tools.zoomToParcel', { locale: 'cs' })}
        </button>
        <button
          type="button"
          className="rh-btn"
          aria-pressed={hideCadastral}
          onClick={() => {
            setHideCadastral((v) => !v);
          }}
          data-testid="parcel-map-toggle-cadastral"
        >
          {hideCadastral
            ? t('webAdmin.parcelMap.tools.showCadastral', { locale: 'cs' })
            : t('webAdmin.parcelMap.tools.hideCadastral', { locale: 'cs' })}
        </button>
        {OPTIONAL_OVERLAY_IDS.map((id) => {
          const overlayLayer = layers?.find((l) => l.id === id);
          const shown = layerState.has(id);
          const keys = OPTIONAL_OVERLAY_TOGGLE_KEYS[id];
          return (
            <button
              key={id}
              type="button"
              className="rh-btn"
              aria-pressed={shown}
              disabled={overlayLayer?.status !== 'available'}
              title={
                overlayLayer?.status === 'available'
                  ? undefined
                  : t('webAdmin.parcelMap.layerUnavailable', {
                      locale: 'cs',
                      values: { reason: overlayLayer?.unavailableReason ?? '' },
                    })
              }
              onClick={() => {
                toggleOverlay(id, overlayLayer?.map?.default_opacity ?? 1);
              }}
              data-testid={`parcel-map-toggle-${id.replace(/_/gu, '-')}`}
            >
              {t(shown ? keys.hide : keys.show, { locale: 'cs' })}
            </button>
          );
        })}
        <button
          type="button"
          className="rh-btn"
          onClick={() => {
            setFullscreen((v) => !v);
          }}
          data-testid="parcel-map-fullscreen"
        >
          {t('webAdmin.parcelMap.tools.fullscreen', { locale: 'cs' })}
        </button>
        <button
          type="button"
          className="rh-btn"
          aria-pressed={swipeEnabled}
          disabled={archiveLayer?.status !== 'available'}
          title={
            archiveLayer?.status === 'available'
              ? undefined
              : t('webAdmin.parcelMap.swipeUnavailable', {
                  locale: 'cs',
                  values: { reason: archiveLayer?.unavailableReason ?? '' },
                })
          }
          onClick={() => {
            setSwipeEnabled((v) => !v);
          }}
          data-testid="parcel-map-swipe"
        >
          {t('webAdmin.parcelMap.tools.swipe', { locale: 'cs' })}
        </button>
      </div>
      )}

      {swipeEnabled && archiveLayer?.status === 'available' && (
        <div
          className="flex items-center gap-2 mb-2 flex-wrap text-sm"
          data-testid="parcel-map-swipe-controls"
        >
          <label>
            {t('webAdmin.parcelMap.swipe.yearLeft', { locale: 'cs' })}{' '}
            <select
              value={swipeYearLeft}
              onChange={(e) => {
                setSwipeYearLeft(e.target.value);
              }}
              data-testid="parcel-map-swipe-year-left"
            >
              {archiveYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('webAdmin.parcelMap.swipe.yearRight', { locale: 'cs' })}{' '}
            <select
              value={swipeYearRight}
              onChange={(e) => {
                setSwipeYearRight(e.target.value);
              }}
              data-testid="parcel-map-swipe-year-right"
            >
              <option value={SWIPE_CURRENT_YEAR}>
                {t('webAdmin.parcelMap.swipe.current', { locale: 'cs' })}
              </option>
              {archiveYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
          <input
            type="range"
            min={0}
            max={100}
            value={swipePosition}
            onChange={(e) => {
              setSwipePosition(Number(e.target.value));
            }}
            aria-label={t('webAdmin.parcelMap.swipe.position', { locale: 'cs' })}
            data-testid="parcel-map-swipe-position"
          />
          <span data-testid="parcel-map-swipe-legend">
            {t('webAdmin.parcelMap.swipe.legend', {
              locale: 'cs',
              values: {
                left: swipeYearLeft,
                right:
                  swipeYearRight === SWIPE_CURRENT_YEAR
                    ? t('webAdmin.parcelMap.swipe.current', { locale: 'cs' })
                    : swipeYearRight,
              },
            })}
          </span>
        </div>
      )}

      {loadError !== null && (
        <p
          className="text-sm m-0 mb-2"
          style={{ color: 'var(--rh-danger)' }}
          data-testid="parcel-map-error"
        >
          {loadError}
        </p>
      )}

      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: fullscreen ? 'calc(100vh - 90px)' : compact ? 200 : 480,
          borderRadius: 10,
          overflow: 'hidden',
          border: '1px solid var(--rh-border)',
        }}
      />

      {!compact && cursor !== null && (
        <div className="text-sm mt-2" data-testid="parcel-map-coordinates">
          <span>
            {t('webAdmin.parcelMap.coordinatesPanel.sjtsk', {
              locale: 'cs',
              values: { x: cursor.sjtsk.x.toFixed(1), y: cursor.sjtsk.y.toFixed(1) },
            })}
          </span>
          {' · '}
          <span>
            {t('webAdmin.parcelMap.coordinatesPanel.wgs84', {
              locale: 'cs',
              values: { lat: cursor.wgs84.lat.toFixed(6), lon: cursor.wgs84.lon.toFixed(6) },
            })}
          </span>
        </div>
      )}

      {!compact && layers !== null && (
        <MapLayersPanel
          layers={layers}
          state={layerState}
          onToggle={toggleOverlay}
          onOpacityChange={setOverlayOpacity}
          zoom={zoom}
        />
      )}

      {/* NAP14 bod 1 (A30): vzdalenosti a pocty objektu v okoli vybrane parcely.
          Vedle panelu vrstev, ne v nem — `MapLayersPanel` je rizena komponenta,
          ktera sama nic nenacita (viz jeji hlavicka), a tahle sekce si data
          tahat musi. Jen v plne mape: v `compact` rezimu neni ani panel vrstev. */}
      {!compact && <ParcelProximityPanel caseId={caseId} />}

      {!compact && layers !== null && (
        <ul
          className="text-sm mt-2"
          style={{ listStyle: 'none', padding: 0, margin: 0 }}
          data-testid="parcel-map-legend"
        >
          {layers.map((l) => (
            <li
              key={l.id}
              data-testid={`parcel-map-legend-${l.id}`}
              style={{ color: l.status === 'available' ? 'var(--rh-ink-soft)' : 'var(--rh-icon-muted)' }}
            >
              {l.status === 'available'
                ? t(LAYER_LABEL_KEY[l.id], { locale: 'cs' })
                : t('webAdmin.parcelMap.layerUnavailable', {
                    locale: 'cs',
                    values: { reason: l.unavailableReason ?? '' },
                  })}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export type { ClientMapLayerId };
